/**
 * Shared utilities for allocation and strategy builders
 */

/**
 * Convert weight from decimal (0.25) to percentage string (25)
 */
export function formatWeightForDisplay(weightDecimal: string | number): string {
    const decimal = typeof weightDecimal === 'string' ? parseFloat(weightDecimal) : weightDecimal;
    const weightPercent = decimal * 100;
    const roundedWeight = Math.round(weightPercent * 100) / 100;
    return roundedWeight.toString();
}

/**
 * Convert weight from percentage string (25) to decimal (0.25)
 */
export function formatWeightForSave(weightPercent: string | number): string {
    const percent = typeof weightPercent === 'string' ? parseFloat(weightPercent) : weightPercent;
    const weightDecimal = percent / 100;
    const roundedWeight = Math.round(weightDecimal * 10000) / 10000;
    return roundedWeight.toString();
}

/**
 * Calculate total weight from an array of items with weight properties
 */
export function calculateTotalWeight(items: Array<{ weight: string }>): number {
    const total = items.reduce((sum, item) => {
        const weight = parseFloat(item.weight || '0');
        return sum + weight;
    }, 0);
    // Round to 2 decimal places to match input precision
    return Math.round(total * 100) / 100;
}

/**
 * Check if a name is unique (case-insensitive, trimmed)
 */
export function isNameUnique(value: string, existingNames: string[], currentName?: string): boolean {
    if (!value) return true;
    const normalizedValue = value.trim().toLowerCase();
    const normalizedCurrent = currentName?.trim().toLowerCase();
    // Allow the current name (when editing)
    if (normalizedValue === normalizedCurrent) return true;
    // Check if name exists in other items
    return !existingNames.some((name) => name.trim().toLowerCase() === normalizedValue);
}

/**
 * Yup test helper for unique names
 */
export function createUniqueNameTest(existingNames: string[], currentName?: string) {
    return function (value: string | undefined) {
        return isNameUnique(value || '', existingNames, currentName);
    };
}

/**
 * Yup test helper for total weight validation (must equal 100%)
 */
export function createTotalWeightTest() {
    return function (value: Array<{ weight: string }> | undefined) {
        if (!value || value.length === 0) {
            return true;
        }
        const total = calculateTotalWeight(value);
        return total === 100;
    };
}
